import React, { useMemo } from "react";
import { motion, type HTMLMotionProps } from "framer-motion";
import { duration, ease, useReducedMotion } from "../../theme/foundations/motion";

export interface MotionRevealProps extends HTMLMotionProps<"div"> {
  /** Seconds (default 0). Stagger ≤ 0.04 per child, ≤ 8 children. */
  delay?: number;
  /** Starting offset in px (default 8). */
  y?: number;
  /** Render as another element, e.g. "section" or "li". */
  as?: keyof JSX.IntrinsicElements;
}

/**
 * Fade-up on mount (360 ms, standard ease). Renders in place under prefers-reduced-motion.
 * @example <MotionReveal delay={0.08}><StatCard … /></MotionReveal>
 */
export const MotionReveal: React.FC<MotionRevealProps> = ({ delay = 0, y = 8, as, children, ...rest }) => {
  const reduce = useReducedMotion();
  const Component = useMemo(
    () => (as ? (motion(as) as unknown as React.ComponentType<HTMLMotionProps<"div">>) : motion.div),
    [as]
  );
  return (
    <Component
      initial={reduce ? false : { opacity: 0, y }}
      animate={{ opacity: 1, y: 0 }}
      transition={reduce ? { duration: 0 } : { duration: duration.slow, ease: ease.standard, delay }}
      {...rest}
    >
      {children}
    </Component>
  );
};
